import { useMemo } from "react";
import { useQueries } from "react-query";
import { fetchStayUpToDateComponentRow } from "../../../components/StayUpToDateComponent/StayUpToDateRenderComponent/useLoadRenderComponent";
import { WEBSITE_TEMPLATE_SURFACE } from "../../../Utils/StayUpToDate/componentSurface";
import type { CarouselData } from "./types";
import {
  CONTENT_SERIES_COMPONENT_TYPE,
  isAutomatedRecentContentCarousel,
} from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";
import {
  predictStayUpToDateTabHasData,
  probeTemplateTabHasFeedItems,
} from "./templateTabEligibility";

export { predictStayUpToDateTabHasData } from "./templateTabEligibility";

function isContentSeriesCarousel(c: CarouselData): boolean {
  return (
    String(c.componentType ?? "").trim().toLowerCase() ===
    CONTENT_SERIES_COMPONENT_TYPE
  );
}

/** Day-window / trending MT carousels — always keep the tab; empty state is in-panel. */
function isPinnedRecentContentCarousel(c: CarouselData): boolean {
  if (isContentSeriesCarousel(c)) return true;
  return isAutomatedRecentContentCarousel({
    componentType: c.componentType,
    componentDisplayName: c.componentDisplayName,
    name: c.title,
    title: c.title,
    shoppable_content_filter: c.shoppableContentFilter,
  });
}

export interface UseTemplateFilteredCarouselsResult {
  visibleCarousels: CarouselData[];
  /** True while component rows are loading (probes refine tabs in the background) */
  isResolvingTabs: boolean;
}

/**
 * 1) Fetch each tab’s stayuptodate/embed row (same cache as the panel).
 * 2) Paint tabs as soon as rows + predict say they can have data.
 * 3) Run feed probes in the background to hide empty tabs — do **not** block
 *    the tab bar / first feed paint on those RPCs (they duplicate the real feed).
 */
export function useTemplateFilteredCarousels(
  carousels: CarouselData[],
  userId: string,
  username: string,
  enabled: boolean,
): UseTemplateFilteredCarouselsResult {
  const usernameLc = (username || "").trim().toLowerCase();

  const withSlug = useMemo(
    () => carousels.filter((c) => Boolean(String(c.renderSlug ?? "").trim())),
    [carousels],
  );

  /** Bio username can hydrate after userId — never show raw tabs while it’s missing. */
  const waitingForUsername = Boolean(
    enabled && userId && withSlug.length > 0 && !usernameLc,
  );

  const rowQueries = useQueries(
    enabled && userId && withSlug.length > 0
      ? withSlug.map((c) => ({
          queryKey: ["getComponent", userId, c.renderSlug, WEBSITE_TEMPLATE_SURFACE],
          queryFn: () =>
            fetchStayUpToDateComponentRow(
              userId,
              String(c.renderSlug),
              WEBSITE_TEMPLATE_SURFACE,
            ),
          staleTime: 60_000,
        }))
      : [],
  );

  const queriesReady =
    withSlug.length > 0 && rowQueries.length === withSlug.length;

  const queryStillLoading = (q: { isLoading: boolean; isFetching: boolean; data?: unknown }) =>
    q.isLoading || (q.isFetching && q.data === undefined);

  const probeQueries = useQueries(
    enabled && userId && usernameLc && queriesReady
      ? withSlug.map((c, i) => ({
          queryKey: [
            "templateTabFeedProbe",
            userId,
            usernameLc,
            c.renderSlug,
          ],
          queryFn: async () => {
            const row =
              (rowQueries[i]?.data as Record<string, unknown> | undefined) ??
              (await fetchStayUpToDateComponentRow(
                userId,
                String(c.renderSlug),
                WEBSITE_TEMPLATE_SURFACE,
              ));
            return probeTemplateTabHasFeedItems(
              userId,
              usernameLc,
              row ?? undefined,
            );
          },
          enabled:
            Boolean(rowQueries[i]?.data) &&
            !queryStillLoading(rowQueries[i]) &&
            !isPinnedRecentContentCarousel(c) &&
            predictStayUpToDateTabHasData(
              rowQueries[i]?.data as Record<string, unknown>,
            ),
          staleTime: 120_000,
        }))
      : [],
  );

  // Block the tab strip only until the first row lands — waiting on every
  // getComponent before paint makes carousels feel late when many tabs exist.
  const anyRowResolved =
    queriesReady && rowQueries.some((q) => !queryStillLoading(q));
  const isResolvingTabs = Boolean(
    waitingForUsername ||
      (enabled &&
        userId &&
        usernameLc &&
        withSlug.length > 0 &&
        !anyRowResolved),
  );

  const visibleCarousels = useMemo(() => {
    // When disabled / missing ids, return [] — never the unfiltered input list.
    // Callers that need a fallback should use their own `carousels` argument.
    if (!enabled || !userId) return [];
    if (waitingForUsername) return [];
    if (!usernameLc) return [];
    if (withSlug.length === 0) return [];
    if (!queriesReady) return [];

    // Progressive: paint tabs whose rows have resolved; skip ones still loading
    // instead of holding the whole strip until N parallel getComponent calls finish.
    return withSlug.filter((_, i) => {
      if (queryStillLoading(rowQueries[i])) return false;

      const row = rowQueries[i]?.data as Record<string, unknown> | undefined;
      if (!predictStayUpToDateTabHasData(row)) return false;

      // Series + MT automated day/trending: show whenever the row resolves.
      // Do not hide when the feed probe returns zero — empty state is shown in-panel.
      // (Empty-type Yesterday / This Week Content must still appear after Add.)
      if (isPinnedRecentContentCarousel(withSlug[i])) return Boolean(row);

      const pq = probeQueries[i];
      // Optimistic: show while probe is pending. Parent also hides empty tabs
      // via onTabFeedResolved once the real feed settles.
      if (!pq || queryStillLoading(pq)) return true;
      if (pq.isError) return true;
      return pq.data === true;
    });
  }, [
    enabled,
    userId,
    usernameLc,
    waitingForUsername,
    withSlug,
    rowQueries,
    queriesReady,
    probeQueries,
  ]);

  return { visibleCarousels, isResolvingTabs };
}
